import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ResponseCodes } from '@provider-portal/common/api/responseCodes';
import { consoleError } from '@provider-portal/common/utils/utils';

import { Island, Link, Typography } from '@bolteu/clay-react';

import {
  credentialsAuthAsync,
  selectAuthError,
  selectAuthRequestState,
} from '@bolteu/redux-session';

import { selectAccountRequestState } from 'rdx/account';
import { $tk } from 'utils/appTranslationKey';

import { LoginForm, LoginFormFields } from 'components/LoginForm/LoginForm';
import { useMetaDescription } from 'root/navigation/useMetaDescription';
import { useLocalizedDocumentTitle } from 'root/navigation/useLocalizedDocumentTitle';

export interface LoginUrlParams {
  token: string;
}

export enum LoginErrorType {
  BAD_CREDENTIALS = 'bad_credentials',
  ACCOUNT_LOCKED = 'account_locked',
  UNKNOWN = 'unkonwn',
}

const rawErrorToLoginError = (error: Error | undefined): LoginErrorType | undefined => {
  if (!error) {
    return undefined;
  }
  if (error.message.includes(ResponseCodes.PROVIDER_ACCOUNT_INVALID_CREDENTIALS.toString())) {
    return LoginErrorType.BAD_CREDENTIALS;
  }
  if (error.message.includes(ResponseCodes.PROVIDER_ACCOUNT_LOCKED.toString())) {
    return LoginErrorType.ACCOUNT_LOCKED;
  }

  consoleError('Unknown login error occured, please add an explicit handler', { error });
  return LoginErrorType.UNKNOWN;
};

const SEO_DESCRIPTION =
  'Access your Bolt Food Merchant account to track performance, update menus, create promotions, and grow your business.';

export const Login = () => {
  useLocalizedDocumentTitle($tk.screens.Login.titleV2);
  useMetaDescription(SEO_DESCRIPTION);

  const intl = useIntl();
  const dispatch = useDispatch();
  const authError = useSelector(selectAuthError);
  const authRequestState = useSelector(selectAuthRequestState);
  const accountRequestState = useSelector(selectAccountRequestState);

  const strings = {
    wrongCredentials: intl.formatMessage({ id: $tk.screens.Login.wrongCredentials }),
    accountLocked: intl.formatMessage({ id: $tk.screens.Login.accountLocked }),
    username: intl.formatMessage({ id: $tk.screens.Login.username }),
    password: intl.formatMessage({ id: $tk.screens.Login.password }),
    rememberMe: intl.formatMessage({ id: $tk.screens.Login.rememberMe }),
    login: intl.formatMessage({ id: $tk.screens.Login.login }),
  };

  const onLoginSubmitHandler = (payload: LoginFormFields) => {
    dispatch(credentialsAuthAsync.request(payload));
  };

  return (
    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
      <div className="w-[85vw] max-w-[450px]">
        <Island elevation="floor-2" className="px-6 py-8">
          <Typography defaultTag="h6" className="bolt-font-heading-xs-accent">
            {intl.formatMessage({ id: $tk.screens.Login.title })}
          </Typography>
          <LoginForm
            strings={strings}
            onSubmit={onLoginSubmitHandler}
            authRequestState={authRequestState}
            accountRequestState={accountRequestState}
            authError={rawErrorToLoginError(authError)}
          />
          <div className="mt-4">
            <Link href="/forgotPassword" className="underline visited:text-link-primary">
              {intl.formatMessage({ id: $tk.screens.Login.forgotPassword })}
            </Link>
          </div>
        </Island>
      </div>
    </div>
  );
};
